/**
 * Email Settings Page
 * Configure SMTP settings and notification toggles (internal/external)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Divider,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { apiGet, apiPut, apiPost } from '../../utils/apiClient';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// Notification type definitions
const NOTIFICATION_TYPES = [
  { key: 'contract_approval', label: 'Contract Approval Request', internalOnly: true },
  { key: 'contract_approved', label: 'Contract Approved', internalOnly: false },
  { key: 'contract_rejected', label: 'Contract Rejected', internalOnly: false },
  { key: 'payment_reminder', label: 'Payment Reminder', internalOnly: false },
  { key: 'overdue_payment', label: 'Overdue Payment', internalOnly: false },
  { key: 'maturity_reminder', label: 'Maturity Reminder', internalOnly: false },
  { key: 'rollover_confirmation', label: 'Rollover Confirmation', internalOnly: false },
  { key: 'rate_change', label: 'Rate Change', internalOnly: false },
];

function EmailSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [settings, setSettings] = useState({});
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiGet('/email/settings');
      if (response.success && response.data) {
        const settingsObj = {};
        response.data.forEach(s => {
          settingsObj[s.setting_key] = s.setting_value;
        });
        setSettings(settingsObj);
      }
    } catch (error) {
      console.error('Error loading email settings:', error);
      setMessage({ type: 'error', text: 'Failed to load email settings' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const trimmedSettings = Object.fromEntries(
        Object.entries(settings).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
      );
      await apiPost('/email/settings/bulk', { settings: trimmedSettings });
      setSettings(trimmedSettings);
      setMessage({ type: 'success', text: 'Email settings saved successfully' });
    } catch (error) {
      console.error('Error saving email settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleToggleChange = (key) => (event) => {
    handleSettingChange(key, event.target.checked ? 'true' : 'false');
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) return;

    try {
      setSendingTest(true);
      await apiPost('/email/test', { recipientEmail: testEmail });
      setMessage({ type: 'success', text: `Test email sent to ${testEmail}` });
      setTestDialogOpen(false);
      setTestEmail('');
    } catch (error) {
      console.error('Error sending test email:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to send test email' });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Email Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure SMTP settings and notification preferences for internal and external recipients
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<SendIcon />}
            onClick={() => setTestDialogOpen(true)}
            disabled={saving}
          >
            Test Email
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadSettings}
            disabled={saving}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}
      {/* Master Switch */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EmailIcon color={settings.email_enabled === 'true' ? 'primary' : 'disabled'} sx={{ fontSize: 40 }} />
          <Box sx={{ flexGrow: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email_enabled === 'true'}
                  onChange={handleToggleChange('email_enabled')}
                  color="primary"
                  size="medium"
                />
              }
              label={
                <Typography variant="h6">
                  Enable Email Notifications
                </Typography>
              }
            />
            <Typography variant="body2" color="text.secondary">
              Master switch - when disabled, no emails will be sent
            </Typography>
          </Box>
          <Chip
            label={settings.email_enabled === 'true' ? 'ENABLED' : 'DISABLED'}
            color={settings.email_enabled === 'true' ? 'success' : 'default'}
          />
        </Box>
      </Paper>
      {/* SMTP Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          SMTP Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure the mail server used to send notifications
        </Typography>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="SMTP Host"
              value={settings.smtp_host || ''}
              onChange={(e) => handleSettingChange('smtp_host', e.target.value)}
              helperText="e.g., smtp.office365.com"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <TextField
              fullWidth
              label="SMTP Port"
              value={settings.smtp_port || ''}
              onChange={(e) => handleSettingChange('smtp_port', e.target.value)}
              helperText="Usually 587 or 465"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.smtp_secure === 'true'}
                  onChange={handleToggleChange('smtp_secure')}
                />
              }
              label="Use SSL/TLS"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="SMTP Username"
              value={settings.smtp_user || ''}
              onChange={(e) => handleSettingChange('smtp_user', e.target.value)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="SMTP Password"
              type="password"
              value={settings.smtp_password || ''}
              onChange={(e) => handleSettingChange('smtp_password', e.target.value)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="From Email"
              value={settings.smtp_from_email || ''}
              onChange={(e) => handleSettingChange('smtp_from_email', e.target.value)}
              helperText="The email address notifications are sent from"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="From Name"
              value={settings.smtp_from_name || ''}
              onChange={(e) => handleSettingChange('smtp_from_name', e.target.value)}
              helperText="Display name for sent emails"
            />
          </Grid>
        </Grid>
      </Paper>
      {/* Notification Type Toggles */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Notification Types
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Control which notification types are sent to internal staff vs external recipients (investors/brokers)
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Notification Type</TableCell>
                <TableCell align="center">Internal Staff</TableCell>
                <TableCell align="center">External (Investors/Brokers)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {NOTIFICATION_TYPES.map((notif) => (
                <TableRow key={notif.key}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {notif.label}
                      {notif.internalOnly && (
                        <Chip label="Internal Only" size="small" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={settings[`internal_${notif.key}`] === 'true'}
                      onChange={handleToggleChange(`internal_${notif.key}`)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {notif.internalOnly ? (
                      <Typography variant="body2" color="text.secondary">N/A</Typography>
                    ) : (
                      <Switch
                        checked={settings[`external_${notif.key}`] === 'true'}
                        onChange={handleToggleChange(`external_${notif.key}`)}
                        color="primary"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button onClick={loadSettings} disabled={saving}>
          Reset
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
      {/* Test Email Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send a test email to verify your SMTP configuration is working correctly.
          </Typography>
          <TextField
            fullWidth
            label="Recipient Email"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="your.email@example.com"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)} disabled={sendingTest}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendTestEmail}
            disabled={!testEmail || sendingTest}
          >
            {sendingTest ? 'Sending...' : 'Send Test Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmailSettingsPage;
